import { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useAuth } from "./AuthProvider";
import environment from "../env";

const CallMeContext = createContext();
export const useCallMe = () => useContext(CallMeContext);

const CallMeProvider = ({ children }) => {
    const { accessToken } = useAuth();
    const apiUrl = environment.CUSTOMER_PORTAL_API;
    const apiKey = environment.CUSTOMER_PORTAL_API_KEY;

    const defaultHeaders = useMemo(() => {
        const headers = {
            "x-api-key": apiKey,
            "organisation": environment.CLIENT_NAME,
            "Content-Type": "application/json",
        };
        if (accessToken) {
            headers["Authorization"] = `Bearer ${accessToken}`;
        }
        return headers;
    }, [apiKey, accessToken]);

    const postCallback = async (request) => {
        console.log("Sending callback request with data:", request);       
        const response = await fetch(`${apiUrl}/callbacks`, {
            method: "POST",
            headers: defaultHeaders,
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error("Error data from API:", errorData);
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Callback request successful:", data);
        return data;
    };

    const getCallbacks = async (clientRef, sessionId = null) => {
        try {
            const headers = {
                "x-api-key": apiKey,
                "organisation": environment.CLIENT_NAME,
                "Content-Type": "application/json",
            };

            if (sessionId) {
                headers["x-session-id"] = sessionId;
            } else if (accessToken) {
                headers["Authorization"] = `Bearer ${accessToken}`;
            }

            const response = await fetch(`${apiUrl}/callbacks/${clientRef}`, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("Error fetching callbacks:", errorData);
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error getting callbacks:", error);
            return [];
        }
    };

    const contextValue = useMemo(
        () => ({ postCallback, getCallbacks }),
        [postCallback]
    );

    return (
        <CallMeContext.Provider value={contextValue}>
            {children}
        </CallMeContext.Provider>
    );
};

CallMeProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default CallMeProvider;
